import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useForm, useFieldArray, type SubmitErrorHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { IconUser, IconPlus, IconTrash, IconCheck, IconAlertCircle, IconDeviceFloppy, IconInfoCircle, IconBrandWhatsapp, IconShieldCheck, IconX, IconUsersGroup } from '@tabler/icons-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DateInput } from '@/components/ui/date-input'
import { format } from 'date-fns'
import { LanguageSelector } from '@/components/guest-portal/LanguageSelector'
import { ReservationInfoWithVariant } from '@/components/guest-portal/reservation-info-variants'
import { DoorCodeSection } from '@/components/guest-portal/DoorCodeSection'
// import { SignaturePad } from '@/components/ui/signature-pad' // TEMPORALMENT DESACTIVAT - veure comentari al codi
import { AddressAutocomplete, AddressData } from '@/components/ui/address-autocomplete'
import { cn } from '@/lib/utils'
import { formatCalendarDate, parseCalendarDate } from '@/lib/dates'
import { useFormPersistence } from '@/hooks/useFormPersistence'
import { toast } from 'sonner'
import { useFormulariPublic } from '@/hooks/useFormulariPublic'
import { useSubmitFormulari } from '@/hooks/useSubmitFormulari'
import { inferSpanishDocumentType, normalizeDocumentType, validateSpanishDocument } from '@/lib/validators/dni.validator'
import { countries, getCountryName, countryCodeToNationality } from '@/lib/data/countries'
import { LegalDisclosure } from '@/components/legal/LegalDisclosure'
import { DebugPanel } from '@/components/dev/DebugPanel'
import { SectionThanks, SectionDoorCode, SectionDirections, SectionCustom } from '@/components/guest-portal/sections'
import { isMossosComplete } from '@/lib/utils/mossos-validation'
import {
  PublicFormSchema,
  DEFAULT_SEXE,
  isMinor,
  requiresDocument,
  type PublicFormData,
  type PublicFormInput,
} from '@/lib/schemas/public-form.schema'
import { translateValidationMessage } from '@/lib/schemas/validation-message'

// Usar tipus del schema Zod: valors dels camps (input) i dades validades (output)
type FormData = PublicFormInput

/** Missatge d'error que torna l'API (cos de la resposta), si n'hi ha */
function apiErrorMessage(error: unknown): string | string[] | undefined {
  const response = (error as { response?: { data?: { message?: string | string[] } } } | null)?.response
  return response?.data?.message
}

/** Text d'un error de camp de React Hook Form, si en porta */
function fieldErrorMessage(error: unknown): string | undefined {
  return (error as { message?: string } | null | undefined)?.message
}

/**
 * Etiqueta i18n de cada camp, en l'ordre en què surten a la pantalla: el toast d'error
 * diu el primer que falla («Viatger 2: Tipus de document») i hi porta la pantalla.
 */
const FIELD_LABEL_KEYS: Record<string, string> = {
  nom: 'form.firstName',
  cognom1: 'form.lastName1',
  telefon: 'form.phone',
  email: 'form.email',
  data_naixement: 'form.dateOfBirth',
  tipus_document: 'form.documentType',
  num_document: 'form.documentNumber',
  data_caducitat_document: 'form.expirationDate',
  ciutat: 'form.residenceCity',
  nacionalitat: 'form.nationality',
  relacio_parentesc: 'form.parentesco',
  sexe: 'form.sex',
  consentiment_rgpd: 'form.privacyPolicy',
}

/** Camps que `isMossosComplete` diu que falten, amb el nom que ja té la pantalla */
const MISSING_FIELD_LABEL_KEYS: Record<string, string> = {
  ...FIELD_LABEL_KEYS,
  cognoms: 'form.lastName1',
  data_caducitat: 'form.expirationDate',
}

export default function FormulariPublicPage() {
  const { token } = useParams<{ token: string }>()
  const { t, i18n } = useTranslation('guestPortal')
  const [success, setSuccess] = useState(false)
  const [showIdespInfo, setShowIdespInfo] = useState(false)

  // Hooks de TanStack Query
  const {
    data: reservaInfo,
    isLoading: loadingForm,
    error: loadError
  } = useFormulariPublic(token)

  const {
    mutateAsync: submitForm,
    isPending: submitting,
    error: submitError
  } = useSubmitFormulari(token)

  const { register, control, handleSubmit, formState: { errors, isValid }, setValue, watch, trigger } = useForm<FormData, unknown, PublicFormData>({
    resolver: zodResolver(PublicFormSchema),
    mode: 'onBlur', // Validar quan l'usuari surt del camp
    defaultValues: {
      viatgers: [{
        nom: '',
        cognom1: '',
        cognom2: '',
        email: '',
        telefon: '',
        mobil: '',
        tipus_document: '', // Sense valor per defecte: l'hoste el tria (no se suposa DNI)
        num_document: '',
        nacionalitat: 'ESP',
        data_naixement: '',
        sexe: DEFAULT_SEXE, // Obligatori per als Mossos; per defecte «Altre» (M0), l'hoste el pot canviar
        adreca: '',
        ciutat: '',
        provincia: '',
        codi_postal: '',
        pais: 'ES',
        address_raw: '',
        empresa: '',
        titol: '',
        peticions_especials: '',
        es_titular: true,
        consentiment_rgpd: false,
        consentiment_marketing: false,
        signature: '',
        camp_suport: '',
        data_caducitat_document: '',
        relacio_parentesc: undefined,
      }]
    }
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'viatgers'
  })

  // Persistencia en localStorage
  const { clearSavedData, hasSavedData } = useFormPersistence({
    key: `formulari-${token}`, // Clave única por token
    watch,
    setValue,
    exclude: [], // No excluir ningún campo
  })

  // Mostrar notificación si hay datos recuperados (solo una vez al montar)
  useEffect(() => {
    if (localStorage.getItem(`formulari-${token}`)) {
      toast.info(t('form.dataRecovered'), {
        description: t('form.dataRecoveredDesc'),
        duration: 5000,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // <-- Array de dependencias vacío para ejecutar solo una vez

  // Número de viajeros ya registrados
  const numRegisteredGuests = reservaInfo?.viatgers_existents?.length || 0

  // Estado para controlar si ya se limpió el formulario inicial
  const [cleanedInitialForm, setCleanedInitialForm] = useState(false)

  // Si TOTS els viatgers ja estan registrats, eliminar el formulari buit inicial.
  // Si en falten, mantenir-lo obert perquè l'usuari el pugui omplir (un a un).
  useEffect(() => {
    if (reservaInfo && !cleanedInitialForm && numRegisteredGuests > 0 && fields.length === 1) {
      const maxGuests = reservaInfo?.portal_config?.maxGuests || reservaInfo?.reserva?.nombre_hostes || 10
      const allRegistered = numRegisteredGuests >= maxGuests
      
      if (allRegistered) {
        // Tots registrats → eliminar formulari buit
        const firstGuest = watch('viatgers.0')
        const isEmpty = !firstGuest?.nom && !firstGuest?.num_document
        if (isEmpty) {
          remove(0)
          setCleanedInitialForm(true)
        }
      }
      // Si en falten → NO eliminar, mantenir obert per al següent viatger
    }
  }, [reservaInfo, cleanedInitialForm, numRegisteredGuests, fields.length, remove, watch])

  // Pre-rellenar formulario SOLO si NO hay viajeros registrados
  useEffect(() => {
    if (reservaInfo && !hasSavedData() && reservaInfo.reserva && numRegisteredGuests === 0) {
      // Separar nombre completo si no hay first_name/last_name
      let firstName = reservaInfo.reserva.guest_first_name || ''
      let lastName = reservaInfo.reserva.guest_last_name || ''

      if (!firstName && !lastName && reservaInfo.reserva.guest_name) {
        const nameParts = reservaInfo.reserva.guest_name.split(' ')
        firstName = nameParts[0] || ''
        lastName = nameParts.slice(1).join(' ') || ''
      }

      // Pre-rellenar el primer viatger con datos de la reserva
      // Separar apellidos si vienen juntos
      const lastNameParts = lastName.split(' ')
      const cognom1 = lastNameParts[0] || ''
      const cognom2 = lastNameParts.slice(1).join(' ') || ''
      
      setValue('viatgers.0.nom', firstName)
      setValue('viatgers.0.cognom1', cognom1)
      setValue('viatgers.0.cognom2', cognom2)
      setValue('viatgers.0.email', reservaInfo.reserva.guest_email || reservaInfo.client?.email || '')
      setValue('viatgers.0.telefon', reservaInfo.reserva.guest_phone || reservaInfo.client?.phone || '')
      setValue('viatgers.0.mobil', reservaInfo.reserva.guest_mobile || reservaInfo.client?.mobile || '')
      setValue('viatgers.0.pais', reservaInfo.reserva.guest_country || reservaInfo.client?.country || 'ES')
      setValue('viatgers.0.nacionalitat', reservaInfo.reserva.guest_nationality || 'ESP')
      setValue('viatgers.0.es_titular', true)
      
      // 🆕 Pre-omplir dades d'adreça de Beds24
      if (reservaInfo.reserva.guest_address) {
        setValue('viatgers.0.adreca', reservaInfo.reserva.guest_address)
      }
      if (reservaInfo.reserva.guest_city) {
        setValue('viatgers.0.ciutat', reservaInfo.reserva.guest_city)
      }
      if (reservaInfo.reserva.guest_postcode) {
        setValue('viatgers.0.codi_postal', reservaInfo.reserva.guest_postcode)
      }
      if (reservaInfo.reserva.guest_state) {
        setValue('viatgers.0.provincia', reservaInfo.reserva.guest_state)
      }

      // 🆕 Geocoding automàtic si té ciutat de Beds24 (per obtenir address_raw estructurat)
      const geocodeAndPrefill = async () => {
        // Aquest efecte només corre sense cap viatger registrat: no hi ha cap `address_raw` desat
        // per reaprofitar (i `GET /formulari/:token` tampoc l'envia).
        // Si té ciutat de Beds24 però NO address_raw → Geocodificar
        const guestCity = reservaInfo.reserva.guest_city
        const guestCountry = reservaInfo.reserva.guest_country
        
        if (guestCity) {
          try {
            const response = await fetch('/api/forms/geocode-city', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ city: guestCity, country: guestCountry }),
            })
            const data = await response.json()
            
            if (data?.formatted_address) {
              setValue('viatgers.0.ciutat', data.locality || guestCity)
              setValue('viatgers.0.pais', data.country_code || 'ES')
              setValue('viatgers.0.address_raw', JSON.stringify(data))
              console.log('✅ Ciutat geocodificada:', data.locality)
            }
          } catch (e) {
            // Fallback: usar text pla de Beds24
            console.warn('Error geocoding, using fallback:', e)
            if (guestCity) setValue('viatgers.0.ciutat', guestCity)
          }
        }
      }

      geocodeAndPrefill()

      console.log('✅ Formulario pre-rellenado:', {
        nombre: firstName,
        apellidos: lastName,
        email: reservaInfo.reserva.guest_email,
        pais: reservaInfo.reserva.guest_country,
        nacionalidad: reservaInfo.reserva.guest_nationality,
        idioma: reservaInfo.reserva.guest_language
      })
    }
  }, [reservaInfo, hasSavedData, setValue, numRegisteredGuests])

  // Traducció dels errors del schema (claus i18n, mai text)
  const tm = (message: string | undefined) => translateValidationMessage(t, message)

  // Número de viatger tal com surt a la capçalera de la targeta
  const travellerNumber = (index: number) =>
    watch(`viatgers.${index}`)?._originalIndex || (numRegisteredGuests + index + 1)

  /**
   * Formulari invàlid: toast en l'idioma de l'hoste amb el primer viatger i camp que
   * fallen, i la pantalla hi va sola (amb diversos viatgers l'error pot quedar a sota).
   */
  const onInvalid: SubmitErrorHandler<FormData> = (formErrors) => {
    const viatgerErrors = formErrors.viatgers
    let first: { index: number; field: string } | null = null

    if (Array.isArray(viatgerErrors)) {
      for (let i = 0; i < viatgerErrors.length && !first; i++) {
        const fieldErrors = viatgerErrors[i] as Record<string, unknown> | undefined
        if (!fieldErrors) continue
        const field =
          Object.keys(FIELD_LABEL_KEYS).find((f) => fieldErrors[f]) ??
          Object.keys(fieldErrors).find((f) => !['root', 'ref', 'type', 'message'].includes(f))
        if (field) first = { index: i, field }
      }
    }

    console.warn('❌ Form validation errors:', first, Object.keys(viatgerErrors ?? {}))

    if (!first) {
      toast.error(t('form.validation.toastTitle'))
      return
    }

    const labelKey = FIELD_LABEL_KEYS[first.field]
    toast.error(t('form.validation.toastTitle'), {
      description: t('form.validation.toastField', {
        n: travellerNumber(first.index),
        field: labelKey ? t(labelKey) : first.field,
      }),
    })

    const target = document.querySelector<HTMLElement>(`[data-field="viatgers.${first.index}.${first.field}"]`)
    if (!target) {
      // No hauria de passar: un camp amagat per edat no pot donar error (requiresDocument)
      console.warn(`Camp amb error sense element visible: viatgers.${first.index}.${first.field}`)
      return
    }
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    target.querySelector<HTMLElement>('input:not([type="hidden"]), button')?.focus({ preventScroll: true })
  }

  /** Canvi del tipus de document: revalida el número si ja tenia error (p. ex. D → O) */
  const onDocumentTypeChange = (index: number, value: string) => {
    setValue(`viatgers.${index}.tipus_document`, value as FormData['viatgers'][number]['tipus_document'], {
      shouldDirty: true,
      shouldValidate: true,
    })
    if (errors.viatgers?.[index]?.num_document) trigger(`viatgers.${index}.num_document`)
  }

  /**
   * En sortir del número: si el tipus és DNI/NIE i el número té el format de l'altre amb
   * la lletra bona, el formulari corregeix el tipus sol (el format mana, §2 de la spec).
   * Si el tipus és buit i el número és un DNI/NIE amb la lletra bona, l'omple (M0): a un
   * espanyol la manca de valor per defecte no li costa cap clic.
   */
  const autocorrectDocumentType = (index: number, numero: string) => {
    const tipus = watch(`viatgers.${index}.tipus_document`)
    if (tipus && tipus !== 'D' && tipus !== 'N') return // P i O no es toquen mai
    const inferit = inferSpanishDocumentType(numero)
    if (inferit && inferit !== tipus && validateSpanishDocument(numero).isValid) {
      setValue(`viatgers.${index}.tipus_document`, inferit, { shouldDirty: true, shouldValidate: true })
    }
  }

  const onSubmit = async (data: PublicFormData) => {
    // Sexe i RGPD ja els exigeix el schema (PublicFormSchema)
    try {
      await submitForm(data)
      setSuccess(true)
      // Limpiar datos guardados en localStorage al enviar exitosamente
      clearSavedData()
      toast.success(t('guestRegistration.successToast'))
      
      // Auto-reload després de 3s per mostrar el següent formulari si en falten viatgers
      setTimeout(() => {
        window.location.reload()
      }, 3000)
    } catch (err) {
      // Error handled by useSubmitFormulari hook state, but we can log or show specific toast here if needed
      console.error('Error submitting form:', err)
    }
  }

  const addGuest = () => {
    append({
      nom: '',
      cognom1: '',
      cognom2: '',
      email: '',
      telefon: '',
      mobil: '',
      tipus_document: '', // Sense valor per defecte: l'hoste el tria (no se suposa DNI)
      num_document: '',
      nacionalitat: 'ESP',
      data_naixement: '',
      sexe: DEFAULT_SEXE, // Per defecte «Altre» (M0)
      adreca: '',
      ciutat: '',
      provincia: '',
      codi_postal: '',
      pais: 'ES',
      address_raw: '',
      empresa: '',
      titol: '',
      peticions_especials: '',
      es_titular: false,
      consentiment_rgpd: false,
      consentiment_marketing: false,
      signature: '', // Firma digital
      camp_suport: '',
      data_caducitat_document: '',
      relacio_parentesc: '',
    })
  }

  if (loadingForm) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common:loading')}</p>
        </div>
      </div>
    )
  }

  if (loadError && !reservaInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
          <IconAlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600 mb-6">{apiErrorMessage(loadError) || t('guestRegistration.loadError')}</p>
          <Button onClick={() => window.location.reload()} className="bg-blue-600 hover:bg-blue-700">
            {t('common:retry', 'Retry')}
          </Button>
        </div>
      </div>
    )
  }

  if (success) {
    // Dates de calendari: el dia de la reserva a qualsevol zona (F4)
    const formatDate = (dateStr: string | undefined) =>
      formatCalendarDate(dateStr, i18n.resolvedLanguage || i18n.language, 'EEE d MMM')

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 py-12 px-4">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Success Message */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 text-center">
            <div className="bg-green-100 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
              <IconCheck className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('guestRegistration.thankYou')}</h1>
            <p className="text-gray-600 mb-6">
              {t('guestRegistration.thankYouMessage')}
            </p>
          </div>

          {/* Door Code Section - Show if available */}
          {reservaInfo?.door_code && (
            <DoorCodeSection
              doorCode={reservaInfo.door_code.code}
              assignedRoom={reservaInfo.door_code.assigned_room || undefined}
              checkInDate={formatDate(reservaInfo.reserva?.data_entrada)}
              checkOutDate={formatDate(reservaInfo.reserva?.data_sortida)}
              propertyName="Hotel Guilleumes"
              defaultCollapsed={false}
              locationInfo={{
                address: "Carretera de Montserrat, km 11",
                city: "Monistrol de Montserrat",
                googleMapsUrl: "https://maps.google.com/?q=Hotel+Guilleumes+Montserrat"
              }}
            />
          )}

          {/* Close window hint */}
          <p className="text-sm text-gray-500 text-center">
            {t('guestRegistration.closeWindow')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-2 md:py-12 px-3 md:px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header con Selector de Idioma */}
        <div className="bg-white rounded-3xl shadow-xl p-2.5 md:p-8 mb-4 md:mb-8">
          <div className="flex items-center justify-between mb-0 md:mb-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-0 md:mb-2 truncate">
                <span className="md:hidden">Guilleumes</span>
                <span className="hidden md:inline">Hotel Guilleumes</span>
              </h1>
              <p className="hidden md:block text-gray-600">
                {t('welcome')}
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 shrink-0">
              {/* WhatsApp Button */}
              <a
                href={`https://wa.me/34938284065?text=${encodeURIComponent(t('whatsapp.defaultMessage', { guestName: reservaInfo?.reserva?.guest_name || '' }))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-full shadow-md hover:shadow-lg transition-all duration-300 shrink-0"
                title={t('whatsapp.contactUs')}
              >
                <IconBrandWhatsapp className="w-5 h-5" />
                <span className="hidden sm:inline text-sm font-medium">WhatsApp</span>
              </a>

              <LanguageSelector />
            </div>
          </div>
          <div className="hidden md:flex items-center justify-between mt-4">
            <p className="text-sm text-gray-500">
              {t('guestRegistration.subtitle')}
            </p>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <IconDeviceFloppy className="h-4 w-4" />
              <span>{t('form.autoSaved')}</span>
            </div>
          </div>
        </div>

        {/* Información de la Reserva - Variant B (Compact) per mòbil */}
        {reservaInfo && reservaInfo.reserva && (
          <div className="mb-6">
            <ReservationInfoWithVariant
              reserva={{
                guest_name: reservaInfo.reserva.guest_name || 'Guest',
                checkIn: reservaInfo.reserva.data_entrada,
                checkOut: reservaInfo.reserva.data_sortida,
                room: {
                  name: reservaInfo.reserva.room || reservaInfo.reserva.allotjament || 'N/A',
                  type: reservaInfo.reserva.room_type ?? undefined
                },
                // Enllaç de grup: els viatgers de totes les reserves, no només els de la mestra
                numAdults: reservaInfo.grup ? reservaInfo.grup.viatgers : reservaInfo.reserva.num_adults || 2,
                numChildren: reservaInfo.grup ? 0 : reservaInfo.reserva.num_children || 0,
                beds24Id: reservaInfo.reserva.beds24_id,
                status: reservaInfo.reserva.estat || 'CONFIRMED'
              }}
              variant="B"
            />
          </div>
        )}

        {/* Enllaç de grup (F8): un sol formulari per a totes les reserves del grup */}
        {reservaInfo?.grup && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6 flex items-center gap-3">
            <div className="bg-blue-100 rounded-full p-2 shrink-0">
              <IconUsersGroup className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-blue-900 font-semibold">
                {t('group.header', { rooms: reservaInfo.grup.habitacions, guests: reservaInfo.grup.viatgers })}
              </p>
              <p className="text-sm text-blue-800">{t('group.hint')}</p>
            </div>
          </div>
        )}

        {/* Banner informatiu: falten viatgers per registrar */}
        {(() => {
          const maxGuests = reservaInfo?.portal_config?.maxGuests || reservaInfo?.reserva?.nombre_hostes || 10
          const remaining = maxGuests - numRegisteredGuests
          if (numRegisteredGuests > 0 && remaining > 0) {
            return (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 flex items-center gap-3">
                <div className="bg-amber-100 rounded-full p-2 shrink-0">
                  <IconAlertCircle className="h-5 w-5 text-amber-600" />
                </div>
                <p className="text-sm text-amber-800 font-medium">
                  {t('guestRegistration.remainingGuests', { count: remaining })}
                </p>
              </div>
            )
          }
          return null
        })()}

        {/* Formulario - Solo mostrar si hay campos por rellenar o se pueden añadir más */}
        {(() => {
          const maxGuests = reservaInfo?.portal_config?.maxGuests || reservaInfo?.reserva?.nombre_hostes || 10
          const allRegistered = numRegisteredGuests >= maxGuests
          const hasPendingGuestForm = fields.some((_, index) => {
            const guest = watch(`viatgers.${index}`)
            return Boolean(guest?._guestId)
          })
          
          // Si todos los viatgers ya están registrados, ocultar formularios vacíos
          // (permet continuar mostrant formulari si s'està completant un viatger existent)
          if (allRegistered && !hasPendingGuestForm) {
            return null
          }
          
          return (
        <form onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate autoComplete="off" className="space-y-6">
          {fields.map((field, index) => {
            const currentGuest = watch(`viatgers.${index}`)
            // Edat exacta (lib/age): document a partir de 14, parentesc per sota de 18
            const showDocument = requiresDocument(currentGuest?.data_naixement)
            const guestIsMinor = isMinor(currentGuest?.data_naixement)

            return (
            <div key={field.id} className="bg-white rounded-3xl shadow-xl p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-3 rounded-2xl">
                    <IconUser className="h-6 w-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {t('guestRegistration.traveler')} {currentGuest?._originalIndex || (numRegisteredGuests + index + 1)}
                    {currentGuest?._guestId && <span className="ml-2 text-sm font-normal text-amber-600">({t('guestRegistration.completing')})</span>}
                  </h2>
                </div>
                {(fields.length > 1 || numRegisteredGuests > 0) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => remove(index)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <IconTrash className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Nombre */}
                <div data-field={`viatgers.${index}.nom`}>
                  <Label htmlFor={`viatgers.${index}.nom`}>{t('form.firstName')} <span className="text-red-500">*</span></Label>
                  <Input
                    {...register(`viatgers.${index}.nom`, { required: t('form.validation.firstNameRequired') })}
                    placeholder={t('form.placeholders.firstName')}
                    autoComplete="off"
                    className={errors.viatgers?.[index]?.nom ? 'border-red-500' : ''}
                  />
                  {errors.viatgers?.[index]?.nom && (
                    <p className="text-sm text-red-500 mt-1">{tm(errors.viatgers[index]?.nom?.message)}</p>
                  )}
                </div>

                {/* Primer Apellido */}
                <div data-field={`viatgers.${index}.cognom1`}>
                  <Label htmlFor={`viatgers.${index}.cognom1`}>{t('form.lastName1')} <span className="text-red-500">*</span></Label>
                  <Input
                    {...register(`viatgers.${index}.cognom1`, { required: t('form.validation.lastName1Required') })}
                    placeholder={t('form.placeholders.lastName')}
                    autoComplete="off"
                    className={errors.viatgers?.[index]?.cognom1 ? 'border-red-500' : ''}
                  />
                  {errors.viatgers?.[index]?.cognom1 && (
                    <p className="text-sm text-red-500 mt-1">{tm(errors.viatgers[index]?.cognom1?.message)}</p>
                  )}
                </div>

                {/* Segundo Apellido - Siempre opcional */}
                <div>
                  <Label htmlFor={`viatgers.${index}.cognom2`}>
                    {t('form.lastName2')}
                  </Label>
                  <Input
                    {...register(`viatgers.${index}.cognom2`)}
                    placeholder=""
                    autoComplete="off"
                    className={errors.viatgers?.[index]?.cognom2 ? 'border-red-500' : ''}
                  />
                  <p className="text-xs text-gray-400 mt-1">{t('form.optional')}</p>
                </div>

                {/* Teléfono (primer — prioritari per comunicació) */}
                <div data-field={`viatgers.${index}.telefon`}>
                  <Label htmlFor={`viatgers.${index}.telefon`}>
                    {t('form.phone')} {!watch(`viatgers.${index}.email`) && <span className="text-red-500">*</span>}
                  </Label>
                  <Input
                    {...register(`viatgers.${index}.telefon`)}
                    type="tel"
                    autoComplete="off"
                    placeholder={t('form.placeholders.phone')}
                    className={errors.viatgers?.[index]?.telefon ? 'border-red-500' : ''}
                  />
                  {errors.viatgers?.[index]?.telefon && (
                    <p className="text-sm text-red-500 mt-1">{tm(errors.viatgers[index]?.telefon?.message)}</p>
                  )}
                  {!watch(`viatgers.${index}.telefon`) && !watch(`viatgers.${index}.email`) && (
                    <p className="text-xs text-gray-400 mt-1">{t('form.phoneOrEmail')}</p>
                  )}
                </div>

                {/* Email (segon — alternatiu al telèfon) */}
                <div data-field={`viatgers.${index}.email`}>
                  <Label htmlFor={`viatgers.${index}.email`}>
                    {t('form.email')} {!watch(`viatgers.${index}.telefon`) && <span className="text-red-500">*</span>}
                  </Label>
                  <Input
                    {...register(`viatgers.${index}.email`)}
                    type="email"
                    autoComplete="off"
                    placeholder={t('form.placeholders.email')}
                    className={errors.viatgers?.[index]?.email ? 'border-red-500' : ''}
                  />
                  {errors.viatgers?.[index]?.email && (
                    <p className="text-sm text-red-500 mt-1">{tm(errors.viatgers[index]?.email?.message)}</p>
                  )}
                </div>

                {/* Fecha de nacimiento - Input DD/MM/AAAA amb auto-focus */}
                <div data-field={`viatgers.${index}.data_naixement`}>
                  <Label htmlFor={`viatgers.${index}.data_naixement`}>
                    {t('form.dateOfBirth')} <span className="text-gray-400 font-normal text-sm">{t('form.dateOfBirthFormat')}</span> <span className="text-red-500">*</span>
                  </Label>
                  <input type="hidden" {...register(`viatgers.${index}.data_naixement`, { required: t('form.validation.birthDateRequired') })} />
                  <DateInput
                    value={parseCalendarDate(watch(`viatgers.${index}.data_naixement`))}
                    onChange={(date) => {
                      // Revalida: els tres camps del DateInput no estan registrats a RHF i sense
                      // això l'error de «data obligatòria» es quedava pintat després d'escriure-la
                      if (date) {
                        setValue(`viatgers.${index}.data_naixement`, format(date, 'yyyy-MM-dd'), { shouldValidate: true, shouldDirty: true })
                      } else {
                        setValue(`viatgers.${index}.data_naixement`, '', { shouldDirty: true })
                      }
                    }}
                    placeholder={{
                      day: t('form.datePlaceholder.day'),
                      month: t('form.datePlaceholder.month'),
                      year: t('form.datePlaceholder.year'),
                    }}
                    minYear={1920}
                    maxYear={new Date().getFullYear()}
                    error={!!errors.viatgers?.[index]?.data_naixement}
                  />
                  {errors.viatgers?.[index]?.data_naixement && (
                    <p className="text-sm text-red-500">{tm(errors.viatgers[index]?.data_naixement?.message)}</p>
                  )}
                </div>

                {/* Menors de 14 (edat exacta): sense document, amb avís */}
                {!showDocument && (
                  <div className="md:col-span-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      {t('form.minorInfo.under14')}
                    </p>
                  </div>
                )}

                {/* Tipo de documento — arrenca buit: l'hoste el tria (un DNI no es pot suposar) */}
                {showDocument && (
                  <div data-field={`viatgers.${index}.tipus_document`}>
                    <Label htmlFor={`viatgers.${index}.tipus_document`}>{t('form.documentType')} <span className="text-red-500">*</span></Label>
                    <Select
                      value={watch(`viatgers.${index}.tipus_document`) || ''}
                      onValueChange={(value) => onDocumentTypeChange(index, value)}
                    >
                      <SelectTrigger className={errors.viatgers?.[index]?.tipus_document ? 'border-red-500' : ''}>
                        <SelectValue placeholder={t('form.selectDocumentType')} />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Códigos oficiales Mossos d'Esquadra (RD 933/2021) */}
                        <SelectItem value="D">{t('form.documentTypes.D')}</SelectItem>
                        <SelectItem value="N">{t('form.documentTypes.N')}</SelectItem>
                        <SelectItem value="P">{t('form.documentTypes.P')}</SelectItem>
                        <SelectItem value="O">{t('form.documentTypes.O')}</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.viatgers?.[index]?.tipus_document && (
                      <p className="text-sm text-red-500 mt-1">{tm(errors.viatgers[index]?.tipus_document?.message)}</p>
                    )}
                  </div>
                )}

                {/* Número de documento — la validació és al schema; en sortir, autocorregeix D/N */}
                {showDocument && (
                  <div data-field={`viatgers.${index}.num_document`}>
                    <Label htmlFor={`viatgers.${index}.num_document`}>{t('form.documentNumber')} <span className="text-red-500">*</span></Label>
                    <Input
                      {...register(`viatgers.${index}.num_document`, {
                        onBlur: (event) => autocorrectDocumentType(index, event.target.value),
                      })}
                      placeholder={t('form.placeholders.documentNumber')}
                      autoComplete="off"
                      className={errors.viatgers?.[index]?.num_document ? 'border-red-500' : ''}
                    />
                    {errors.viatgers?.[index]?.num_document && (
                      <p className="text-sm text-red-500 mt-1">{tm(errors.viatgers[index]?.num_document?.message)}</p>
                    )}
                  </div>
                )}

                {/* 
                  ============================================================
                  CAMP IDESP / NÚMERO DE SOPORTE - TEMPORALMENT DESACTIVAT
                  ============================================================
                  
                  Motiu: El camp IDESP (Identificador Electrònic de Suport Personal) 
                  NO és obligatori per al registre de viatgers als Mossos d'Esquadra 
                  segons el RD 933/2021 actual.
                  
                  El camp existeix al DNI espanyol (3 lletres + 6 números a la part 
                  posterior), però la seva inclusió al formulari:
                  - Genera confusió als usuaris (molts no saben on trobar-lo)
                  - No és requerit per la normativa vigent
                  - Pot ser activat en el futur si la normativa canvia
                  
                  Per reactivar: Descomentar el bloc següent i afegir la validació
                  corresponent al backend.
                  
                  Data desactivació: 27-NOV-2025
                  ============================================================
                */}
                {/* IDESP FIELD - COMMENTED OUT
                {(() => {
                  const docType = watch(`viatgers.${index}.tipus_document`)

                  if (!showDocument) return null // requiresDocument: edat exacta, com el schema
                  if (docType !== 'D') return null // Solo DNI, no NIE
                  
                  return (
                    <div>
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`viatgers.${index}.camp_suport`}>{t('form.supportNumber')} <span className="text-red-500">*</span></Label>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-blue-600 bg-blue-100 rounded-full hover:bg-blue-200 transition-colors"
                          onClick={() => setShowIdespInfo(true)}
                          title={t('form.supportNumberInfoTitle')}
                        >
                          <IconInfoCircle className="w-4 h-4" />
                        </button>
                      </div>
                      <Input
                        {...register(`viatgers.${index}.camp_suport`, {
                          required: t('form.validation.supportRequired')
                        })}
                        placeholder={t('form.placeholders.supportNumber')}
                        className={errors.viatgers?.[index]?.camp_suport ? 'border-red-500' : ''}
                      />
                      {errors.viatgers?.[index]?.camp_suport && (
                        <p className="text-sm text-red-500 mt-1">{errors.viatgers[index]?.camp_suport?.message}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {t('form.supportNumberHelp')}
                      </p>
                    </div>
                  )
                })()}
                */}

                {/* Fecha de Caducidad (solo para Pasaporte P y mayores de 14) — opcional: no és a
                    l'annex I del RD 933/2021, el V24 no l'envia i l'AEPD la considera dada de més */}
                {(() => {
                  const docType = watch(`viatgers.${index}.tipus_document`)

                  if (!showDocument) return null
                  if (docType !== 'P') return null

                  return (
                    <div data-field={`viatgers.${index}.data_caducitat_document`}>
                      <Label htmlFor={`viatgers.${index}.data_caducitat_document`}>{t('form.expirationDate')}</Label>
                      <Input
                        type="date"
                        {...register(`viatgers.${index}.data_caducitat_document`)}
                      />
                      <p className="text-xs text-gray-400 mt-1">{t('form.optional')}</p>
                    </div>
                  )
                })()}

                {/* Municipio de residencia + Nacionalidad - En fila */}
                <div>
                  <input type="hidden" {...register(`viatgers.${index}.ciutat`)} />
                  <AddressAutocomplete
                    apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                    granularity="locality"
                    countries={[]}
                    label={t('form.residenceCity')}
                    placeholder={t('form.placeholders.address')}
                    hint={t('form.addressHint')}
                    loadingHint={t('form.addressLoading')}
                    language={i18n.language}
                    value={watch(`viatgers.${index}.ciutat`)}
                    onChange={(address: AddressData) => {
                      setValue(`viatgers.${index}.ciutat`, address.locality || '', {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: false
                      })
                      setValue(`viatgers.${index}.pais`, address.country_code || '', {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true
                      })
                      setValue(`viatgers.${index}.address_raw`, JSON.stringify(address), {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: false
                      })
                      // Auto-fill nacionalitat des del país del municipi
                      if (address.country_code) {
                        const iso3Code = countryCodeToNationality(address.country_code)
                        if (iso3Code) {
                          setValue(`viatgers.${index}.nacionalitat`, iso3Code, {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true
                          })
                        }
                      }
                    }}
                    required
                  />
                </div>

                {/* Nacionalidad */}
                <div data-field={`viatgers.${index}.nacionalitat`}>
                  <Label htmlFor={`viatgers.${index}.nacionalitat`}>{t('form.nationality')} <span className="text-red-500">*</span></Label>
                  <input type="hidden" {...register(`viatgers.${index}.nacionalitat`, { required: t('form.validation.nationalityRequired') })} />
                  <Select
                    value={watch(`viatgers.${index}.nacionalitat`) || 'ESP'}
                    onValueChange={(value) => setValue(`viatgers.${index}.nacionalitat`, value, { shouldValidate: true, shouldDirty: true })}
                  >
                    <SelectTrigger className={errors.viatgers?.[index]?.nacionalitat ? 'border-red-500' : ''}>
                      <SelectValue placeholder={t('form.selectNationality')}>
                        {watch(`viatgers.${index}.nacionalitat`) 
                          ? getCountryName(watch(`viatgers.${index}.nacionalitat`), i18n.language)
                          : t('form.selectNationality')
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {countries.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name[i18n.language as keyof typeof country.name] || country.name.en} ({country.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.viatgers?.[index]?.nacionalitat && (
                    <p className="text-sm text-red-500 mt-1">{tm(errors.viatgers[index]?.nacionalitat?.message)}</p>
                  )}
                </div>

                {/* País de residencia - Hidden, s'omple automàticament des de Google Maps */}
                <input type="hidden" {...register(`viatgers.${index}.pais`)} />
                {/* Raw Address Data - Hidden */}
                <input type="hidden" {...register(`viatgers.${index}.address_raw`)} />

                {/* Relación de Parentesco (solo para menores de 18) */}
                {(() => {
                  if (!guestIsMinor) return null // Edat exacta (lib/age), com el schema

                  return (
                    <div className="md:col-span-2 p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                      <p className="text-sm text-amber-800">
                        {t('form.minorInfo.under18')}
                      </p>
                      <div data-field={`viatgers.${index}.relacio_parentesc`}>
                        <Label htmlFor={`viatgers.${index}.relacio_parentesc`}>{t('form.parentesco')} <span className="text-red-500">*</span></Label>
                        <Select
                          value={watch(`viatgers.${index}.relacio_parentesc`) as string | undefined}
                          onValueChange={(value) => setValue(`viatgers.${index}.relacio_parentesc`, value, { shouldValidate: true, shouldDirty: true })}
                        >
                          <SelectTrigger className={errors.viatgers?.[index]?.relacio_parentesc ? 'border-red-500' : ''}>
                            <SelectValue placeholder={t('form.placeholders.selectRelation')} />
                          </SelectTrigger>
                          <SelectContent>
                            {/* Códigos oficiales Mossos d'Esquadra (RD 933/2021) - Lista completa */}
                            <SelectItem value="PM">{t('form.parentescoOptions.PM')}</SelectItem>
                            <SelectItem value="TU">{t('form.parentescoOptions.TU')}</SelectItem>
                            <SelectItem value="AB">{t('form.parentescoOptions.AB')}</SelectItem>
                            <SelectItem value="BA">{t('form.parentescoOptions.BA')}</SelectItem>
                            <SelectItem value="TI">{t('form.parentescoOptions.TI')}</SelectItem>
                            <SelectItem value="HR">{t('form.parentescoOptions.HR')}</SelectItem>
                            <SelectItem value="CY">{t('form.parentescoOptions.CY')}</SelectItem>
                            <SelectItem value="HJ">{t('form.parentescoOptions.HJ')}</SelectItem>
                            <SelectItem value="NI">{t('form.parentescoOptions.NI')}</SelectItem>
                            <SelectItem value="BN">{t('form.parentescoOptions.BN')}</SelectItem>
                            <SelectItem value="SB">{t('form.parentescoOptions.SB')}</SelectItem>
                            <SelectItem value="CD">{t('form.parentescoOptions.CD')}</SelectItem>
                            <SelectItem value="SG">{t('form.parentescoOptions.SG')}</SelectItem>
                            <SelectItem value="YN">{t('form.parentescoOptions.YN')}</SelectItem>
                            <SelectItem value="OT">{t('form.parentescoOptions.OT')}</SelectItem>
                          </SelectContent>
                        </Select>
                        {errors.viatgers?.[index]?.relacio_parentesc && (
                          <p className="text-sm text-red-500 mt-1">{tm(errors.viatgers[index]?.relacio_parentesc?.message)}</p>
                        )}
                      </div>
                    </div>
                  )
                })()}

                {/* Sexe */}
                <div data-field={`viatgers.${index}.sexe`}>
                  <Label htmlFor={`viatgers.${index}.sexe`}>{t('form.sex')}</Label>
                  <input type="hidden" {...register(`viatgers.${index}.sexe`)} />
                  <Select
                    value={watch(`viatgers.${index}.sexe`) || ''}
                    onValueChange={(value) => setValue(`viatgers.${index}.sexe`, value as FormData['viatgers'][number]['sexe'], { shouldDirty: true, shouldTouch: true, shouldValidate: true })}
                  >
                    <SelectTrigger className={errors.viatgers?.[index]?.sexe ? 'border-red-500' : ''}>
                      <SelectValue placeholder={t('form.placeholders.select')} />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Códigos oficiales Mossos d'Esquadra (RD 933/2021) */}
                      <SelectItem value="M">{t('form.sexOptions.M')}</SelectItem>
                      <SelectItem value="F">{t('form.sexOptions.F')}</SelectItem>
                      <SelectItem value="OTHER">{t('form.sexOptions.OTHER')}</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.viatgers?.[index]?.sexe && (
                    <p className="text-sm text-red-500 mt-1">{tm(errors.viatgers[index]?.sexe?.message)}</p>
                  )}
                </div>


              </div>

              {/* Información Legal RGPD + Mossos */}
              <div className="mt-6">
                <LegalDisclosure lang={i18n.language} />
              </div>

              {/* 
                ============================================================
                SIGNATURA DIGITAL - TEMPORALMENT DESACTIVADA
                ============================================================
                
                Motiu: La signatura digital del viatger NO és un requisit 
                legal obligatori per al registre de viatgers als Mossos 
                d'Esquadra segons el RD 933/2021.
                
                Raons per desactivar-la temporalment:
                - Afegeix fricció innecessària al procés de registre
                - Pot ser problemàtica en dispositius mòbils
                - No aporta valor legal addicional sense certificat digital
                - El consentiment RGPD (checkbox) és suficient legalment
                
                Casos d'ús futurs on podria ser útil:
                - Contractes de lloguer d'apartaments
                - Autoritzacions de menors
                - Documents que requereixin signatura física
                
                Per reactivar: Descomentar el bloc següent i afegir 
                `signature` com a camp required a la validació del form.
                
                Data desactivació: 27-NOV-2025
                ============================================================
              */}
              {/* SIGNATURE PAD - COMMENTED OUT
              <div className="mt-6">
                <SignaturePad
                  value={watch(`viatgers.${index}.signature`)}
                  onChange={(sig) => setValue(`viatgers.${index}.signature`, sig)}
                  label={`${t('form.signature')} *`}
                  required
                />
              </div>
              */}

              {/* Checkboxes */}
              <div className="mt-6 space-y-4">
                {/* 
                  NOTA: Checkbox "és titular" comentat - només editable per admin
                  El primer viatger es marca automàticament com a titular (es_titular: true)
                  Aquest camp NO ha de ser visible ni editable pels usuaris públics
                  
                <label className="flex items-center space-x-3 cursor-pointer group">
                  <Checkbox
                    id={`viatgers.${index}.es_titular`}
                    checked={watch(`viatgers.${index}.es_titular`)}
                    onCheckedChange={(checked) => setValue(`viatgers.${index}.es_titular`, checked as boolean)}
                    className="h-5 w-5"
                  />
                  <span className="text-sm font-normal group-hover:text-blue-600 transition-colors">
                    {t('form.isTitular')}
                  </span>
                </label>
                */}

                {/* Botó RGPD gran reversible */}
                <button
                  type="button"
                  data-field={`viatgers.${index}.consentiment_rgpd`}
                  onClick={() => setValue(`viatgers.${index}.consentiment_rgpd`, !watch(`viatgers.${index}.consentiment_rgpd`), { shouldValidate: true })}
                  className={cn(
                    'w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer min-h-[56px]',
                    watch(`viatgers.${index}.consentiment_rgpd`)
                      ? 'bg-green-50 border-green-400 hover:bg-green-100'
                      : 'bg-red-50 border-red-300 hover:bg-red-100'
                  )}
                >
                  <div className="flex items-center gap-3">
                    {watch(`viatgers.${index}.consentiment_rgpd`) ? (
                      <div className="bg-green-500 rounded-full p-1.5">
                        <IconShieldCheck className="h-5 w-5 text-white" />
                      </div>
                    ) : (
                      <div className="bg-red-400 rounded-full p-1.5">
                        <IconShieldCheck className="h-5 w-5 text-white" />
                      </div>
                    )}
                    <span className={cn(
                      'font-medium text-sm sm:text-base',
                      watch(`viatgers.${index}.consentiment_rgpd`) ? 'text-green-800' : 'text-red-800'
                    )}>
                      {watch(`viatgers.${index}.consentiment_rgpd`)
                        ? (t('form.rgpdAccepted') + ' ✓')
                        : (t('form.acceptRgpd') + ' *')
                      }
                    </span>
                  </div>
                  {watch(`viatgers.${index}.consentiment_rgpd`) && (
                    <IconX className="h-4 w-4 text-green-600 hover:text-green-800 shrink-0" />
                  )}
                </button>
                {errors.viatgers?.[index]?.consentiment_rgpd && (
                  <p className="text-sm text-red-500 mt-1">{tm(errors.viatgers[index]?.consentiment_rgpd?.message)}</p>
                )}

                {/* Eliminado checkbox marketing según Q&A */}
              </div>
              
              {/* Submit button - Dentro del contenedor del viajero, después de RGPD */}
              {index === fields.length - 1 && (
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-14 text-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg rounded-xl"
                  >
                    {submitting ? t('guestRegistration.submitting') : t('guestRegistration.submit')}
                  </Button>
                </div>
              )}
            </div>
          )})}
          

          {/* Botón añadir viajero - Solo si no se ha llegado al máximo */}
          {(() => {
            const maxGuests = reservaInfo?.portal_config?.maxGuests || reservaInfo?.reserva?.nombre_hostes || 10
            const totalGuests = numRegisteredGuests + fields.length
            const canAddMore = totalGuests < maxGuests
            
            return canAddMore ? (
              <Button
                type="button"
                variant="outline"
                onClick={addGuest}
                className="w-full border-2 border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50 h-16 text-gray-600 hover:text-blue-600"
              >
                <IconPlus className="h-5 w-5 mr-2" />
                {t('guestRegistration.addGuest')}
              </Button>
            ) : null // No mostrar mensaje si ya se llegó al máximo, el botón submit ya está dentro
          })()}

          {/* ============================================================
              SECCIONS ADDICIONALS - Configurades via GuestPortal Config
              ============================================================ */}
          {(() => {
            const sectionsConfig = reservaInfo?.portal_config?.sectionsConfig
            const hasAnySectionEnabled = sectionsConfig && (
              sectionsConfig.thanks?.enabled ||
              sectionsConfig.doorCode?.enabled ||
              sectionsConfig.directions?.enabled ||
              sectionsConfig.custom?.enabled
            )
            
            // Si no hi ha cap secció habilitada, no mostrar res
            if (!hasAnySectionEnabled) return null
            
            return (
              <div className="mt-8 space-y-4">
                <h2 className="text-lg font-semibold text-gray-700 mb-4">
                  {t('sections.additionalInfo')}
                </h2>
                
                {/* Secció Agraïments - Visible quan tots registrats */}
                {sectionsConfig?.thanks?.enabled && (
                  <SectionThanks locked={true} />
                )}
                
                {/* Secció Codi d'Accés - Visible X hores abans check-in */}
                {sectionsConfig?.doorCode?.enabled && (
                  <SectionDoorCode locked={true} />
                )}
                
                {/* Secció Com Arribar - Sempre visible (configurable) */}
                {sectionsConfig?.directions?.enabled && (
                  <SectionDirections locked={true} />
                )}
                
                {/* Secció Personalitzada - Per reserva específica */}
                {sectionsConfig?.custom?.enabled && (
                  <SectionCustom locked={true} />
                )}
              </div>
            )
          })()}

          {/* Debug Panel - Solo visible en desarrollo */}
          <DebugPanel
            data={watch()}
            title="Debug: Form Data"
            validationErrors={Object.entries(errors).flatMap(([key, value]) => {
              if (key === 'viatgers' && Array.isArray(value)) {
                return value.flatMap((v, i) => 
                  Object.entries(v || {}).map(([field, err]) => ({
                    path: ['viatgers', String(i), field],
                    message: fieldErrorMessage(err) || 'Error'
                  }))
                )
              }
              return [{ path: [key], message: fieldErrorMessage(value) || 'Error' }]
            })}
          />

          {/* Error message */}
          {submitError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-red-700">{apiErrorMessage(submitError) || t('guestRegistration.submitError')}</p>
            </div>
          )}
        </form>
          )
        })()}

        {/* Viatgers ya registrados */}
        {reservaInfo && reservaInfo.viatgers_existents && reservaInfo.viatgers_existents.length > 0 && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl shadow-lg p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 bg-green-100 rounded-full">
                <IconCheck className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-green-800">
                  {t('guestRegistration.alreadyRegistered', { count: reservaInfo.viatgers_existents.length })}
                </h3>
                <p className="text-sm text-green-600">{t('guestRegistration.registeredSubtitle')}</p>
              </div>
            </div>
            <div className="space-y-2">
              {reservaInfo.viatgers_existents.map((viatger, index) => {
                const validation = isMossosComplete(viatger)
                const isComplete = validation.isComplete

                return (
                  <div
                    key={index}
                    className={`flex items-center gap-3 text-sm rounded-xl p-3 border ${
                      isComplete
                        ? 'bg-white/80 border-green-100'
                        : 'bg-amber-50/80 border-amber-200'
                    }`}
                  >
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full font-medium ${
                      isComplete
                        ? 'bg-green-100 text-green-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {viatger.nom} {viatger.cognoms}
                      </div>
                      {viatger.num_document && (() => {
                        const tipus = normalizeDocumentType(viatger.tipus_document)
                        const label = tipus === 'D' ? 'DNI' : tipus === 'N' ? 'NIE' : t(`form.documentTypes.${tipus === 'P' ? 'P' : 'O'}`)
                        return (
                          <div className="text-xs text-gray-500">
                            {label}: {viatger.num_document}
                          </div>
                        )
                      })()}
                      {!isComplete && (
                        <div className="text-xs text-amber-600 mt-1">
                          ⚠️ {t('guestRegistration.incompleteData')}:{' '}
                          {validation.missingFields
                            .map((f) => (MISSING_FIELD_LABEL_KEYS[f] ? t(MISSING_FIELD_LABEL_KEYS[f]) : f))
                            .join(', ')}
                        </div>
                      )}
                    </div>
                    {isComplete ? (
                      <IconCheck className="h-5 w-5 text-green-500" />
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-amber-600 border-amber-300 hover:bg-amber-50"
                        onClick={() => {
                          // Afegir el viatger al formulari amb les dades existents
                          append({
                            _guestId: viatger.id, // Marcar per actualització
                            _originalIndex: index + 1, // Guardar índex original (1-based)
                            nom: viatger.nom || '',
                            cognom1: viatger.cognoms.split(' ')[0] || '',
                            cognom2: viatger.cognoms.split(' ').slice(1).join(' ') || '',
                            email: viatger.email || '',
                            telefon: viatger.telefon || '',
                            mobil: viatger.mobil || '',
                            // Conserva el tipus desat (àlies → D/N/P/O); sense tipus, buit (no se suposa DNI)
                            tipus_document: normalizeDocumentType(viatger.tipus_document),
                            num_document: viatger.num_document || '',
                            nacionalitat: viatger.nacionalitat || 'ESP',
                            data_naixement: viatger.data_naixement || '',
                            // El valor desat tal com arriba (la resposta el porta com a text)
                            sexe: (viatger.sexe || DEFAULT_SEXE) as FormData['viatgers'][number]['sexe'],
                            // `GET /formulari/:token` no torna el número de suport: sempre buit
                            camp_suport: '',
                            pais: viatger.pais || 'ES',
                            es_titular: viatger.es_titular || false,
                            consentiment_rgpd: false,
                            consentiment_marketing: false,
                          })
                          // Scroll al formulari
                          setTimeout(() => {
                            document.querySelector('form')?.scrollIntoView({ behavior: 'smooth' })
                          }, 100)
                        }}
                      >
                        {t('guestRegistration.complete')}
                      </Button>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Si ya se llegó al máximo, mostrar mensaje y botón DENTRO del box verde */}
            {(() => {
              const maxGuests = reservaInfo?.portal_config?.maxGuests || reservaInfo?.reserva?.nombre_hostes || 10
              const allRegistered = reservaInfo.viatgers_existents.length >= maxGuests

              return allRegistered ? (
                <div className="mt-4 pt-4 border-t border-green-200">
                  <p className="text-center text-green-700 font-medium mb-3">
                    ✅ {t('guestRegistration.allGuestsRegistered', { count: reservaInfo.viatgers_existents.length })}
                  </p>
                </div>
              ) : null
            })()}
          </div>
        )}
      </div>
      
      {/* Modal IDESP Info */}
      <Dialog open={showIdespInfo} onOpenChange={setShowIdespInfo}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IconInfoCircle className="w-5 h-5 text-blue-600" />
              {t('form.supportNumberInfoTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Imagen del DNI */}
            <div className="rounded-lg overflow-hidden border border-gray-200">
              <img 
                src="/images/dni-idesp-example.png" 
                alt={t('idesp.imageAlt')}
                className="w-full h-auto"
              />
            </div>
            
            {/* Explicación */}
            <div className="space-y-2 text-sm text-gray-600">
              <p className="font-medium text-gray-900">
                📋 {t('idesp.whatIs')}
              </p>
              <p dangerouslySetInnerHTML={{ __html: t('idesp.description') }} />
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-blue-800">
                  📍 <strong>{t('idesp.location')}:</strong> {t('idesp.locationDetail')}
                </p>
                <p className="text-blue-800 mt-1">
                  📝 <strong>{t('idesp.format')}:</strong> {t('idesp.formatDetail')}
                </p>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {t('idesp.mandatory')}
              </p>
            </div>
            
            <Button 
              onClick={() => setShowIdespInfo(false)}
              className="w-full"
            >
              {t('idesp.understood')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
